import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiCalendar, FiMapPin } from 'react-icons/fi';
import { FaRegClock, FaUsers, FaMoneyBill } from 'react-icons/fa';
import i18n from 'i18next';
import eventUtils, { formatEventTime, formatFriendlyEventDate, getEventAttendees } from '../../utils/eventUtils';
import { getFormattedStartEndDate } from '../../utils/dateUtils';
import { PersonalEvent as Event } from '../../types/event';
import InfoBox from '../misc/InfoBox';
import ApiImage from '../misc/ApiImage';
import { formatMoney } from '../../utils/formatUtils';

interface Props {
  event: Event;
  children?: React.ReactNode;
  style?: string;
}

function EventOverview({ event, style = '', children }: Props): JSX.Element {
  const { t } = useTranslation();
  const {
    title,
    description,
    startTime,
    endTime,
    location,
    locationUrl,
    maximumAttendees,
    organizer,
    deadline,
    isRecurring,
    priceOfTicket,
  } = event;

  const numberOfAttendees = getEventAttendees(event);
  const deadlineIsPassed = deadline ? new Date() > new Date(deadline) : false;
  const maximumAttendeesReached = maximumAttendees ? numberOfAttendees >= maximumAttendees : false;

  return (
    <div className={`flex h-full w-full flex-col items-center p-8 ${style}`}>
      {event.picture ? (
        <ApiImage
          src={event.picture}
          alt={title}
          className="aspect-square w-2/5 rounded-lg bg-primary-300 object-contain p-4"
        />
      ) : (
        <img
          src={`${process.env.PUBLIC_URL}/images/figures/phone.svg`}
          alt="Event"
          className="aspect-square w-2/5 rounded-lg bg-primary-300 object-contain p-4"
        />
      )}
      <h1 className="mt-4 text-center text-3xl font-semibold text-primary-900">{title}</h1>
      <div className="mt-6 flex w-full justify-between">
        <div className="flex flex-row items-center gap-4">
          <img
            src={`${process.env.PUBLIC_URL}/images/figures/phone.svg`}
            alt="Organizer"
            className="h-8 w-8 rounded-full bg-primary-300"
          />
          <div>
            <h3 className="text-sm font-semibold text-primary-900 text-opacity-50">
              {t('page.events.infoScreen.organizedBy', { name: '' }).replace(organizer, '').trim()}
            </h3>
            <h3>{organizer}</h3>
          </div>
        </div>
        {maximumAttendees && (
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-row items-center justify-center">
              <div className="relative z-30 flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-[#fdf6f4] bg-primary-300">
                <FaUsers className="text-primary-300" size={16} />
              </div>
            </div>
            <div className="flex flex-col">
              <h3 className="text-sm font-semibold text-primary-900 text-opacity-50">
                {t('page.events.infoScreen.attendees')}
              </h3>
              <h3 className="font-medium">
                {t('page.events.infoScreen.maximumAttendees', { attendees: numberOfAttendees, max: maximumAttendees })}
              </h3>
            </div>
          </div>
        )}
      </div>
      <div className="mt-4 w-full text-left">
        <div className="flex w-full flex-col gap-2">
          {!event.isDatePicker && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center rounded bg-secondary-200 p-1 text-primary">
                  <FiCalendar size={16} />
                </div>
                <span className="text-sm font-medium text-primary-900">
                  {getFormattedStartEndDate(startTime, endTime)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center rounded bg-secondary-200 p-1 text-primary">
                  <FaRegClock size={16} />
                </div>
                <span className="text-sm font-medium text-primary-900">{eventUtils.formatDate(event, 'time')}</span>
              </div>
            </>
          )}

          {deadline && (
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center rounded bg-secondary-200 p-1 text-primary">
                <FiCalendar size={16} />
              </div>
              <span className="text-sm font-medium text-primary-900">
                {t('page.events.infoScreen.deadline', {
                  date: `${formatFriendlyEventDate(new Date(deadline))} ${formatEventTime(new Date(deadline))}`,
                })}
              </span>
            </div>
          )}
          {isRecurring && (
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center rounded bg-secondary-200 p-1 text-primary">
                <FaRegClock size={16} />
              </div>
              <span className="text-sm font-medium text-primary-900">
                {t('page.events.infoScreen.recurring', {
                  frequency: i18n.t(`page.events.infoScreen.frequencies.${event.frequency.toLowerCase()}`),
                })}
              </span>
            </div>
          )}
          {location && (
            <a href={locationUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:underline">
              <div className="flex items-center justify-center rounded bg-secondary-200 p-1 text-primary">
                <FiMapPin size={16} />
              </div>
              <span className="text-sm font-medium text-primary-900">{location}</span>
            </a>
          )}
          {priceOfTicket && (
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center rounded bg-secondary-200 p-1 text-primary">
                <FaMoneyBill size={16} />
              </div>
              <span className="text-sm font-medium text-primary-900">
                {formatMoney(priceOfTicket / 100, i18n.language)}
              </span>
            </div>
          )}
        </div>
        {description && (
          <>
            <h2 className="mt-4 text-lg font-semibold text-primary-900">About {title}</h2>
            <p className="text-md max-h-32 overflow-y-auto text-primary-900 text-opacity-50">{description}</p>
          </>
        )}
      </div>
      {deadlineIsPassed && <InfoBox label={t('page.events.infoScreen.deadlinePassed')} />}
      {maximumAttendeesReached && <InfoBox label={t('page.events.infoScreen.maximumAttendeesReached')} />}
      {children} {/* Children like attendance icons, custom fields form or date picker options */}
    </div>
  );
}

export default EventOverview;
